import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { validatedApi } from '../../../lib/api/validated-client';
import { useToast } from '../../../hooks/useToast';

export interface OvertimePendingItem {
  id: string;
  venueId: string;
  venueName: string;
  customerName: string;
  startTime: string;
  endTime: string;
  overtimeMinutes: number;
  overtimeStartedAt: string | null;
  baseAmount: number;
  inventoryTotal: number;
  duration: number;
  bookingSource: string | null;
}

/**
 * POST /bookings/:id/overtime
 * Idempotent — marks a CHECKED_IN booking as overtime.
 * Safe to call multiple times; server ignores if already started.
 */
export function useStartOvertime(venueId?: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (bookingId: string) => {
      await validatedApi.post<void>(`/bookings/${bookingId}/overtime`, {});
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bookings', 'floor-status', venueId] });
    },
    onError: () => {
      // Silently swallow — the 5-min cron is the safety net
    },
  });
}

/**
 * POST /bookings/:id/complete-overtime
 * Idempotent — transitions CHECKED_IN booking to COMPLETED and records overtime minutes.
 * Safe to call multiple times.
 */
export function useCompleteOvertime(venueId?: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (bookingId: string) => {
      await validatedApi.post<void>(`/bookings/${bookingId}/complete-overtime`, {});
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bookings', 'floor-status', venueId] });
      void queryClient.invalidateQueries({ queryKey: ['bookings', 'overtime-pending'] });
    },
    onError: () => {
      // Silently swallow — the 5-min cron is the safety net
    },
  });
}

/**
 * POST /bookings/:id/overtime-decision
 * Vendor decides to CHARGE or WAIVE overtime after a session auto-completes.
 */
export function useOvertimeDecision(bookingId: string, venueId?: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<void, Error, 'CHARGE' | 'WAIVE'>({
    mutationFn: async (decision: 'CHARGE' | 'WAIVE') => {
      await validatedApi.post<void>(`/bookings/${bookingId}/overtime-decision`, { decision });
    },
    onSuccess: (_, decision) => {
      void queryClient.invalidateQueries({ queryKey: ['bookings', 'floor-status', venueId] });
      void queryClient.invalidateQueries({ queryKey: ['bookings', 'overtime-pending'] });
      toast.success(decision === 'CHARGE' ? 'Overtime charge recorded' : 'Overtime waived');
    },
    onError: (error) => {
      toast.showError(error);
    },
  });
}

/**
 * GET /bookings/vendor/overtime-pending
 * Returns all COMPLETED bookings with overtimeMinutes > 0 and no charge/waive decision yet.
 */
export function useOvertimePending(venueId?: string) {
  return useQuery<OvertimePendingItem[]>({
    queryKey: ['bookings', 'overtime-pending', venueId],
    queryFn: async () => {
      return validatedApi.get<OvertimePendingItem[]>('/bookings/vendor/overtime-pending');
    },
    enabled: !!venueId,
    refetchInterval: 15000,
    staleTime: 10000,
  });
}
